import type {
  ApiData,
  ApiErrorPayload,
  LiveSession,
  ProbeRequestReceipt,
  RelayHealth,
  RoomRecord,
  SessionPage,
} from "../shared/types";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  const payload = (await response.json().catch(() => null)) as ApiData<T> | ApiErrorPayload | null;
  if (!response.ok) {
    const error = payload && "error" in payload ? payload.error : null;
    throw new ApiError(error?.message ?? `请求失败（${response.status}）`, response.status, error?.code ?? "UNKNOWN");
  }

  if (!payload || !("data" in payload)) {
    throw new ApiError("服务器返回了无法识别的数据", response.status, "INVALID_RESPONSE");
  }
  return payload.data;
}

const json = (value: unknown) => JSON.stringify(value);

export const api = {
  getSession: () => request<{ authenticated: boolean }>("/api/auth/session"),
  login: (password: string) =>
    request<{ authenticated: true }>("/api/auth/login", { method: "POST", body: json({ password }) }),
  logout: () => request<{ authenticated: false }>("/api/auth/logout", { method: "POST", body: json({}) }),
  listRooms: (includeArchived = false) =>
    request<RoomRecord[]>(`/api/rooms${includeArchived ? "?includeArchived=true" : ""}`),
  createRoom: (input: string, alias: string, enabled: boolean) =>
    request<RoomRecord>("/api/rooms", { method: "POST", body: json({ input, alias, enabled }) }),
  updateRoom: (roomId: string, values: { input?: string; alias: string; enabled: boolean }) =>
    request<RoomRecord>(`/api/rooms/${roomId}`, { method: "PUT", body: json(values) }),
  archiveRoom: (roomId: string) =>
    request<RoomRecord>(`/api/rooms/${roomId}`, { method: "DELETE", body: json({}) }),
  restoreRoom: (roomId: string) =>
    request<RoomRecord>(`/api/rooms/${roomId}/restore`, { method: "POST", body: json({}) }),
  probeRoom: (roomId: string) =>
    request<ProbeRequestReceipt>(`/api/rooms/${roomId}/probe`, { method: "POST", body: json({}) }),
  getRelayStatus: () => request<RelayHealth>("/api/relay/status"),
  listSessions: (roomId: string, cursor?: string | null) => {
    const params = new URLSearchParams({ roomId, limit: "20" });
    if (cursor) params.set("cursor", cursor);
    return request<SessionPage>(`/api/sessions?${params}`);
  },
};

export type { LiveSession, ProbeRequestReceipt, RelayHealth, RoomRecord };
